import { useQuery } from '@tanstack/react-query';
import { GlobalReactQueryOptions } from '../../config';
import { useMemo, useRef } from 'react';
import { FetchAggregationsQuickSearch } from '../../api/listContent/fetchAggregationsQuickSearch';
import { Aggregations } from '../../../models/Aggregations';
import { mapAggregations } from '../../api/listContent/mapAggregations';
import { ProductFilter } from '../../store/api';

type UseAggregationsQuickSearchQuery = {
    readonly status: 'error' | 'loading' | 'success' | 'idle';
    readonly error: any;
    readonly data: Aggregations;
    readonly isFetching: boolean;
};

type UseAggregationsQuickSearchOptions = {
    readonly filterKey: string;
    readonly searchQuery?: string;
    readonly filterMap?: ProductFilter;
    readonly fetchFunc: FetchAggregationsQuickSearch;
};

const emptyAggregations = {};

export const useAggregationsQuickSearchQuery = ({
    filterKey = '',
    searchQuery,
    filterMap,
    fetchFunc
}: UseAggregationsQuickSearchOptions): UseAggregationsQuickSearchQuery => {
    const lastData = useRef<Aggregations>({});
    if (!searchQuery) {
        lastData.current = {};
    }

    const quickSearchFilterMap = useMemo(
        () => ({
            ...filterMap,
            [filterKey]: {
                quicksearchQuery: searchQuery
            }
        }),
        [filterMap, filterKey, searchQuery]
    );

    const { status, data, error, isFetching } = useQuery(
        ['aggregationsQuickSearch', filterKey, quickSearchFilterMap],
        () =>
            fetchFunc({ filterKey, quickSearchQuery: searchQuery!, filters: quickSearchFilterMap })
                .then(data => mapAggregations(data))
                .then(data => markEmptyAggregationsQuickSearch(data)),
        {
            ...GlobalReactQueryOptions,
            enabled: !!searchQuery,
            initialData: lastData.current,
            onSuccess: (aggregations: Aggregations) => (lastData.current = aggregations),

            // we do not want to cache quick searches!
            // enabling caching might cause cache hits from a different page
            cacheTime: 0
        }
    );

    return {
        isFetching,
        status,
        error,
        data: data ?? emptyAggregations
    };
};

const markEmptyAggregationsQuickSearch = (data: Aggregations): Aggregations => {
    return Object.keys(data).reduce<Aggregations>((next, category) => {
        const aggregation = data![category];

        return {
            ...next,
            [category]: {
                ...aggregation,
                buckets: aggregation?.buckets ?? []
            }
        };
    }, {});
};
